import { atom } from 'nanostores';
import type { UserData } from '~/utils/OauthHelper';

// Define the initial state using that type
interface IntegrationState {
  userData: UserData | null;
}

// Create a store with the initial state
const userDataStore = atom<IntegrationState>({ userData: null });

// Function to set user data
export function setUserData(userData: UserData) {
  // console.log('setting user data', userData);
  userDataStore.set({ userData });
  console.log('User data set to:', userDataStore.get());
}

// Function to clear user data
export function clearUserData() {
  userDataStore.set({ userData: null });
  console.log('User data cleared:', userDataStore.get());
}

export const githubOwnerStore = atom<string>('');
export const githubRepoStore = atom<string>('');
export const githubBranchStore = atom<string>('');
export const githubPlatformStore = atom<string>('');

// Supabase stores
export const supabaseProjectIdStore = atom<string>('');
export const supabaseProjectNameStore = atom<string>('');
export const supabaseAnonKeyStore = atom<string>('');
export const supabaseOrganizationIdStore = atom<string>('');
export const supabaseOrganizationNameStore = atom<string>('');
export const supabaseConnectionStatusStore = atom<{
  isConnected: boolean;
  isDisconnected: boolean;
}>({
  isConnected: false,
  isDisconnected: false,
});

// Function to set all github data
export function setAllGithubData(owner: string, repo: string, branch: string) {
  console.log('>>>[setAllGithubData] owner:', owner);
  console.log('>>>[setAllGithubData] repo:', repo);
  console.log('>>>[setAllGithubData] branch:', branch);
  githubOwnerStore.set(owner);
  githubRepoStore.set(repo);
  githubBranchStore.set(branch);
  console.log('GitHub data set to:', {
    owner: githubOwnerStore.get(),
    repo: githubRepoStore.get(),
    branch: githubBranchStore.get(),
    platform: githubPlatformStore.get(),
  });
}

// Function to set all Supabase data
export function setAllSupabaseData(
  projectId: string,
  projectName: string,
  anonKey: string,
  organizationId: string,
  organizationName: string,
) {
  console.log('>>>[setAllSupabaseData] Setting Supabase data');
  supabaseProjectIdStore.set(projectId);
  supabaseProjectNameStore.set(projectName);
  supabaseAnonKeyStore.set(anonKey);
  supabaseOrganizationIdStore.set(organizationId);
  supabaseOrganizationNameStore.set(organizationName);
  console.log('Supabase data set to:', {
    projectId: supabaseProjectIdStore.get(),
    projectName: supabaseProjectNameStore.get(),
    anonKey: supabaseAnonKeyStore.get(),
    organizationId: supabaseOrganizationIdStore.get(),
    organizationName: supabaseOrganizationNameStore.get(),
  });
}

// Function to update Supabase connection status
export function updateSupabaseConnectionStatus(isConnected?: boolean, isDisconnected?: boolean, storedToken?: string) {
  storedToken = storedToken ?? localStorage.getItem('supabase_access_token') ?? '';

  const projectId = supabaseProjectIdStore.get();
  const anonKey = supabaseAnonKeyStore.get();

  isConnected = isConnected ?? (!!projectId && !!anonKey && !!storedToken);
  isDisconnected = isDisconnected ?? (!!projectId && !!anonKey && !storedToken);

  console.log('>>>[updateSupabaseConnectionStatus] storedToken:', storedToken);
  console.log('>>>[updateSupabaseConnectionStatus] projectId:', projectId);
  console.log('>>>[updateSupabaseConnectionStatus] anonKey:', anonKey);
  console.log('>>>[updateSupabaseConnectionStatus] isConnected:', isConnected);
  console.log('>>>[updateSupabaseConnectionStatus] isDisconnected:', isDisconnected);

  console.log('>>>[updateSupabaseConnectionStatus] setting Supabase connection status');

  supabaseConnectionStatusStore.set({
    isConnected,
    isDisconnected,
  });

  console.log('Supabase connection status updated:', { isConnected, isDisconnected });
}

// Function to set Supabase connection status
export function setSupabaseConnectionStatus(isConnected: boolean, isDisconnected: boolean) {
  supabaseConnectionStatusStore.set({
    isConnected,
    isDisconnected,
  });
  console.log('Supabase connection status set:', { isConnected, isDisconnected });
}

// Function to clear all github data
export function clearAllGithubData() {
  githubOwnerStore.set('');
  githubRepoStore.set('');
  githubBranchStore.set('');
  console.log('GitHub data cleared:', {
    owner: githubOwnerStore.get(),
    repo: githubRepoStore.get(),
    branch: githubBranchStore.get(),
  });
}

// Function to clear all Supabase data
export function clearAllSupabaseData() {
  supabaseProjectIdStore.set('');
  supabaseProjectNameStore.set('');
  supabaseAnonKeyStore.set('');
  supabaseOrganizationIdStore.set('');
  supabaseOrganizationNameStore.set('');
  console.log('Supabase data cleared');
}

// Function to set github owner
export function setGithubOwner(owner: string) {
  githubOwnerStore.set(owner);
  console.log('GitHub owner set to:', githubOwnerStore.get());
}

export function setGithubRepo(repo: string) {
  githubRepoStore.set(repo);
  console.log('GitHub repo set to:', githubRepoStore.get());
}

export function setGithubBranch(branch: string) {
  githubBranchStore.set(branch);
  console.log('GitHub branch set to:', githubBranchStore.get());
}

export function setGithubPlatform(platform: string) {
  githubPlatformStore.set(platform);
  console.log('GitHub platform set to:', githubPlatformStore.get());
}

// Supabase individual setters
export function setSupabaseProjectId(projectId: string) {
  supabaseProjectIdStore.set(projectId);
  console.log('Supabase project ID set to:', supabaseProjectIdStore.get());
}

export function setSupabaseProjectName(projectName: string) {
  supabaseProjectNameStore.set(projectName);
  console.log('Supabase project name set to:', supabaseProjectNameStore.get());
}

export function setSupabaseAnonKey(anonKey: string) {
  supabaseAnonKeyStore.set(anonKey);
  console.log('Supabase anon key has been set');
}

export function setSupabaseOrganizationId(orgId: string) {
  supabaseOrganizationIdStore.set(orgId);
  console.log('Supabase organization ID set to:', supabaseOrganizationIdStore.get());
}

export function setSupabaseOrganizationName(orgName: string) {
  supabaseOrganizationNameStore.set(orgName);
  console.log('Supabase organization name set to:', supabaseOrganizationNameStore.get());
}

export function clearGithubBranch() {
  githubBranchStore.set('');
  console.log('GitHub branch cleared:', githubBranchStore.get());
}

export function clearGithubRepo() {
  githubRepoStore.set('');
  console.log('GitHub repo cleared:', githubRepoStore.get());
}

export function clearGithubOwner() {
  githubOwnerStore.set('');
  console.log('GitHub owner cleared:', githubOwnerStore.get());
}

export function clearGithubPlatform() {
  githubPlatformStore.set('');
  console.log('GitHub platform cleared:', githubPlatformStore.get());
}

// Supabase individual clear
export function clearSupabaseProjectId() {
  supabaseProjectIdStore.set('');
  console.log('Supabase project ID cleared');
}

export function clearSupabaseProjectName() {
  supabaseProjectNameStore.set('');
  console.log('Supabase project name cleared');
}

export function clearSupabaseAnonKey() {
  supabaseAnonKeyStore.set('');
  console.log('Supabase anon key cleared');
}

export function clearSupabaseOrganizationId() {
  supabaseOrganizationIdStore.set('');
  console.log('Supabase organization ID cleared');
}

export function clearSupabaseOrganizationName() {
  supabaseOrganizationNameStore.set('');
  console.log('Supabase organization name cleared');
}

export const clearGithubStores = () => {
  githubOwnerStore.set('');
  githubRepoStore.set('');
  githubBranchStore.set('');
  githubPlatformStore.set('');
};

export const clearSupabaseStores = () => {
  supabaseProjectIdStore.set('');
  supabaseProjectNameStore.set('');
  supabaseAnonKeyStore.set('');
  supabaseOrganizationIdStore.set('');
  supabaseOrganizationNameStore.set('');
};

// API Spec stores - used for one-time prompt injection then cleared
export const apiSpecStore = atom<string>('');
export const apiSpecYamlStore = atom<boolean>(false);
export const apiSpecMethodStore = atom<string>('Fetch');

export function setApiSpec(spec: string) {
  apiSpecStore.set(spec);
}

export function setApiSpecYaml(shouldGenerateYaml: boolean) {
  apiSpecYamlStore.set(shouldGenerateYaml);
}

export function setApiSpecMethod(method: string) {
  apiSpecMethodStore.set(method);
}

export function setAllApiSpecData(spec: string, shouldGenerateYaml: boolean, method: string) {
  apiSpecStore.set(spec);
  apiSpecYamlStore.set(shouldGenerateYaml);
  apiSpecMethodStore.set(method);
}

export function clearApiSpecData() {
  apiSpecStore.set('');
  apiSpecYamlStore.set(false);
  apiSpecMethodStore.set('Fetch');
}

export function hasApiSpec(): boolean {
  return apiSpecStore.get().length > 0;
}

// Export userDataStore
export { userDataStore };
